import { Container, Graphics, Sprite } from 'pixi.js';
import { getTexture } from '@/assets/resolver';
import { COLORS, WEAPON_CATEGORY_COLOR } from '@/config/colors';
import { WEAPONS } from '@/config/weapons';
import { weaponGlyphKey } from '@/config/weapon-visuals';
import { weaponDps } from '@/entities/weapon-dps';
import { t } from '@/i18n';
import { drawCardIcon } from './card-icons';
import { arrowGlyph, deltaGlyph } from './hangar-icons';
import type { ShopState } from './shop-types';
import { chamferPanel, hexPath, label } from './widgets';

/** The three numbers a weapon decision needs (docs/09 wave 4). */
export interface WeaponNumbers {
  /** Ship total: every socket's direct + area DPS */
  power: number;
  /** Shots/s of the socket in question */
  rate: number;
  /** Area DPS of the socket in question (0 = no blast) */
  area: number;
}

/** Live DPS of one socket inside a given loadout (rack penalty included). */
export function socketNumbers(s: ShopState, equipped: ReadonlyArray<string>, id: string | null) {
  if (!id) return { dps: 0, rate: 0, area: 0 };
  const d = weaponDps(id, { gunTier: s.ctx.weapon.gunTier, capacity: s.equipCapacity, equipped }, s.ctx.stats);
  return d ? { dps: d.dps + d.aoeDps, rate: d.rate, area: d.aoeDps } : { dps: 0, rate: 0, area: 0 };
}

/** One socket's share of the ship's power in the current loadout. */
export function socketPower(s: ShopState, socket: number): number {
  return socketNumbers(s, s.equipped, s.equipped[socket] ?? null).dps;
}

export function shipPower(s: ShopState, equipped: ReadonlyArray<string>): number {
  return equipped.reduce((sum, id) => sum + socketNumbers(s, equipped, id).dps, 0);
}

/** Socket `i` with weapon `id` put into it (an empty socket appends). */
export function loadoutWith(s: ShopState, id: string, socket: number): string[] {
  const eq = [...s.equipped];
  if (socket < eq.length) eq[socket] = id;
  else eq.push(id);
  return eq;
}

/** Before → after numbers of equipping `id` into `socket`. */
export function compareEquip(
  s: ShopState, id: string, socket: number,
): { before: WeaponNumbers; after: WeaponNumbers } {
  const old = s.equipped[socket] ?? null;
  const next = loadoutWith(s, id, socket);
  const b = socketNumbers(s, s.equipped, old);
  const a = socketNumbers(s, next, id);
  return {
    before: { power: shipPower(s, s.equipped), rate: b.rate, area: b.area },
    after: { power: shipPower(s, next), rate: a.rate, area: a.area },
  };
}

/**
 * Where a bag weapon goes by default: the first EMPTY open socket, else the
 * socket whose swap gives the most ship power (the game suggests the best
 * move; selecting a socket first pins it instead).
 */
export function bestSocket(s: ShopState, id: string): number {
  if (s.equipped.length < s.equipCapacity) return s.equipped.length;
  let best = 0;
  let bestPower = -Infinity;
  for (let i = 0; i < s.equipped.length; i++) {
    const p = shipPower(s, loadoutWith(s, id, i));
    if (p > bestPower + 1e-6) {
      bestPower = p;
      best = i;
    }
  }
  return best;
}

export const COMPARE_W = 392;
export const COMPARE_H = 84;
const BLOCK_X = 128;
const BLOCK_W = 88;

/** Signed, rounded delta text: "+24", "-3", "+0.4". */
function signed(n: number, decimals: number): string {
  const v = decimals > 0 ? n.toFixed(decimals) : String(Math.round(n));
  return n >= 0 ? `+${v}` : v;
}

function fmt(n: number, decimals: number): string {
  return decimals > 0 ? n.toFixed(decimals) : String(Math.round(n));
}

/** Stat block: glyph, BIG value, coloured ▲/▼ delta under it. */
function statBlock(
  c: Container, x: number, glyph: (g: Graphics) => void, value: number, delta: number | null,
  decimals: number,
): void {
  const g = new Graphics();
  glyph(g);
  g.position.set(x + 16, 26);
  c.addChild(g);
  const v = label(fmt(value, decimals), 22, 0xffffff, 1);
  v.anchor.set(0, 0.5);
  v.position.set(x + 34, 26);
  if (v.width > BLOCK_W - 36) v.scale.set((BLOCK_W - 36) / v.width);
  c.addChild(v);
  if (delta === null) return;
  const eps = decimals > 0 ? 0.05 : 0.5;
  const flat = Math.abs(delta) < eps;
  const color = flat ? 0x8a9099 : delta > 0 ? COLORS.statUp : COLORS.statDown;
  const d = new Graphics();
  if (!flat) deltaGlyph(d, x + 16, 60, 8, color, delta > 0);
  else d.roundRect(x + 9, 58, 14, 4, 2).fill(color);
  c.addChild(d);
  const dt = label(flat ? '=' : signed(delta, decimals), 16, color, 1);
  dt.anchor.set(0, 0.5);
  dt.position.set(x + 32, 60);
  c.addChild(dt);
}

/** Ring burst = area damage (a blast, no reading needed). */
function areaGlyph(g: Graphics, color: number): void {
  g.circle(0, 0, 5).fill(color);
  g.circle(0, 0, 10).stroke({ color, width: 2.5 });
  g.circle(0, 0, 15).stroke({ color, width: 2, alpha: 0.6 });
}

/**
 * Comparison strip under the sockets. With a bag weapon (or the waiting
 * crate) focused: its glyph → target socket number, then ship POWER, RATE
 * and AREA as big numbers with green ▲ / red ▼ deltas. With a socket
 * focused: that weapon's own numbers. Otherwise the ship's total power.
 */
export function buildCompare(
  s: ShopState, focus: { weaponId: string; socket: number } | { socket: number } | null,
): Container {
  const c = new Container();
  const g = new Graphics();
  chamferPanel(g, COMPARE_W, COMPARE_H, { fill: 0x0f0c1a, stroke: 0x2a2440, strokeWidth: 2, notch: 12 });
  c.addChild(g);
  const power = (gr: Graphics) => drawCardIcon(gr, 'damage', COLORS.statPower).scale.set(0.5);
  const rate = (gr: Graphics) => drawCardIcon(gr, 'firerate', COLORS.statTempo).scale.set(0.5);
  const area = (gr: Graphics) => areaGlyph(gr, WEAPON_CATEGORY_COLOR.bomb);

  if (focus && 'weaponId' in focus) {
    const def = WEAPONS[focus.weaponId];
    if (!def) return c;
    const cmp = compareEquip(s, focus.weaponId, focus.socket);
    const glyph = new Sprite(getTexture(weaponGlyphKey(def.id)));
    glyph.anchor.set(0.5);
    glyph.scale.set(0.62);
    glyph.position.set(34, COMPARE_H / 2);
    c.addChild(glyph);
    const m = new Graphics();
    arrowGlyph(m, 74, COMPARE_H / 2, 9, 0xe8eaf0);
    hexPath(m, 102, COMPARE_H / 2, 15).fill(COLORS.playerYellow);
    c.addChild(m);
    const n = label(String(focus.socket + 1), 18, 0x0f0c1a, 0);
    n.anchor.set(0.5);
    n.position.set(102, COMPARE_H / 2 + 1);
    c.addChild(n);
    const { before: b, after: a } = cmp;
    statBlock(c, BLOCK_X, power, a.power, a.power - b.power, 0);
    statBlock(c, BLOCK_X + BLOCK_W, rate, a.rate, a.rate - b.rate, 1);
    statBlock(c, BLOCK_X + BLOCK_W * 2, area, a.area, a.area - b.area, 0);
    return c;
  }
  const id = focus ? s.equipped[focus.socket] ?? null : null;
  const head = new Graphics();
  if (id) {
    const def = WEAPONS[id]!;
    const glyph = new Sprite(getTexture(weaponGlyphKey(def.id)));
    glyph.anchor.set(0.5);
    glyph.scale.set(0.62);
    glyph.position.set(56, COMPARE_H / 2);
    c.addChild(glyph);
    const one = socketNumbers(s, s.equipped, id);
    statBlock(c, BLOCK_X, power, one.dps, null, 0);
    statBlock(c, BLOCK_X + BLOCK_W, rate, one.rate, null, 1);
    statBlock(c, BLOCK_X + BLOCK_W * 2, area, one.area, null, 0);
    return c;
  }
  // Nothing weapon-related in focus: the ship's total power, nothing else
  drawCardIcon(head, 'damage', COLORS.statPower).scale.set(0.7);
  head.position.set(48, COMPARE_H / 2);
  c.addChild(head);
  const total = label(String(Math.round(shipPower(s, s.equipped))), 30, 0xffffff, 1);
  total.anchor.set(0, 0.5);
  total.position.set(80, COMPARE_H / 2);
  const cap = label(t('shop.shipPower'), 12, 0x8a9099, 2);
  cap.anchor.set(0, 0.5);
  cap.position.set(96 + total.width, COMPARE_H / 2 + 4);
  c.addChild(total, cap);
  return c;
}
